/**
 * Add To Cart Use Case
 *
 * Application use case for adding a product variant to the cart.
 */

import { ICartService } from '@/domain/services/cart.service.interface';
import { CartItemEntity } from '@/domain/entities/cart-item.entity';
import type { CartEntity } from '@/domain/entities/cart.entity';
import type { CartItemSubscriptionInput } from '@template-project/storefront-bff-client';

export interface AddToCartInput {
  cartToken?: string;
  productId: string;
  variantId: string;
  quantity: number;
  subscription?: CartItemSubscriptionInput;
  options?: {
    countryCode?: string;
    currency?: string;
    currencyRatio?: number;
    vendor?: string;
    shop?: string;
    upsellFunnelId?: string;
    upsellFirstStepId?: string;
    funnelOfferToken?: string;
  };
}

export interface AddToCartOutput {
  success: boolean;
  cartToken: string;
  /** Authoritative post-mutation snapshot; present for the BFF implementation. */
  cart?: CartEntity;
}

export class AddToCartUseCase {
  constructor(private readonly cartService: ICartService) {}

  private shouldRecoverMissingCart(message: string): boolean {
    return message.includes('Cart not found') || message.includes('404');
  }

  async execute(input: AddToCartInput): Promise<AddToCartOutput> {
    if (!input.productId) {
      throw new Error('Product ID is required');
    }

    if (!input.variantId) {
      throw new Error('Variant ID is required');
    }

    if (!input.quantity || input.quantity < 1) {
      throw new Error('Quantity must be at least 1');
    }

    const cartItem: CartItemEntity = {
      product_id: input.productId,
      variant_id: input.variantId,
      quantity: input.quantity,
      ...(input.subscription ? { subscription: input.subscription } : {}),
    };

    if (input.cartToken) {
      // Try adding to existing cart; if it no longer exists, create a new one
      try {
        const result = await this.cartService.addToCart(input.cartToken, cartItem, input.options);
        return result;
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        if (this.shouldRecoverMissingCart(message)) {
          // Only a confirmed missing cart may be recreated under its reserved token.
          // Transient update failures must surface so an existing cart is never
          // mistaken for a safe create path.
          const result = await this.cartService.createCartWithItem(cartItem, {
            ...input.options,
            cartToken: input.cartToken,
          });
          return result;
        }
        throw error;
      }
    } else {
      // Create new cart with item
      const result = await this.cartService.createCartWithItem(cartItem, input.options);
      return result;
    }
  }
}
